// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/pages/travel-wallet/types.ts
// 🔧 PURPOSE: Valute, categorie e forma dei dati del Portafoglio, più il
//   salvataggio. I dati stanno sotto `nva-wallet:<prenotazione>`: due
//   soggiorni diversi non si mescolano più in un unico elenco di spese.
//   Ogni importo porta con sé anche il valore in euro del momento in cui è
//   stato inserito, così i totali non cambiano quando cambia il cambio.
// ============================================

import { LEGACY_WALLET_KEY, walletStorageKey, legacyMigrationDone, markLegacyMigrationDone } from '../../lib/budgetMath';

// --- Valute -------------------------------------------------------------

export interface CurrencyInfo {
  code: string;
  name: string;
  nameIt: string;
  symbol: string;
  flag: string;
}

/** Le prime otto sono le "popolari" del selettore, poi il resto. */
export const CURRENCIES: CurrencyInfo[] = [
  { code: 'EUR', name: 'Euro', nameIt: 'Euro', symbol: '€', flag: '🇪🇺' },
  { code: 'USD', name: 'US Dollar', nameIt: 'Dollaro USA', symbol: '$', flag: '🇺🇸' },
  { code: 'GBP', name: 'British Pound', nameIt: 'Sterlina Britannica', symbol: '£', flag: '🇬🇧' },
  { code: 'CHF', name: 'Swiss Franc', nameIt: 'Franco Svizzero', symbol: 'CHF', flag: '🇨🇭' },
  { code: 'PLN', name: 'Polish Zloty', nameIt: 'Zloty Polacco', symbol: 'zł', flag: '🇵🇱' },
  { code: 'JPY', name: 'Japanese Yen', nameIt: 'Yen Giapponese', symbol: '¥', flag: '🇯🇵' },
  { code: 'CNY', name: 'Chinese Yuan', nameIt: 'Yuan Cinese', symbol: '¥', flag: '🇨🇳' },
  { code: 'BRL', name: 'Brazilian Real', nameIt: 'Real Brasiliano', symbol: 'R$', flag: '🇧🇷' },
  { code: 'CAD', name: 'Canadian Dollar', nameIt: 'Dollaro Canadese', symbol: 'C$', flag: '🇨🇦' },
  { code: 'AUD', name: 'Australian Dollar', nameIt: 'Dollaro Australiano', symbol: 'A$', flag: '🇦🇺' },
  { code: 'SEK', name: 'Swedish Krona', nameIt: 'Corona Svedese', symbol: 'kr', flag: '🇸🇪' },
  { code: 'NOK', name: 'Norwegian Krone', nameIt: 'Corona Norvegese', symbol: 'kr', flag: '🇳🇴' },
  { code: 'DKK', name: 'Danish Krone', nameIt: 'Corona Danese', symbol: 'kr', flag: '🇩🇰' },
  { code: 'CZK', name: 'Czech Koruna', nameIt: 'Corona Ceca', symbol: 'Kč', flag: '🇨🇿' },
  { code: 'HUF', name: 'Hungarian Forint', nameIt: 'Fiorino Ungherese', symbol: 'Ft', flag: '🇭🇺' },
  { code: 'RON', name: 'Romanian Leu', nameIt: 'Leu Rumeno', symbol: 'lei', flag: '🇷🇴' },
  { code: 'BGN', name: 'Bulgarian Lev', nameIt: 'Lev Bulgaro', symbol: 'лв', flag: '🇧🇬' },
  { code: 'HRK', name: 'Croatian Kuna', nameIt: 'Kuna Croata', symbol: 'kn', flag: '🇭🇷' },
  { code: 'TRY', name: 'Turkish Lira', nameIt: 'Lira Turca', symbol: '₺', flag: '🇹🇷' },
  { code: 'MXN', name: 'Mexican Peso', nameIt: 'Peso Messicano', symbol: 'MX$', flag: '🇲🇽' },
  { code: 'INR', name: 'Indian Rupee', nameIt: 'Rupia Indiana', symbol: '₹', flag: '🇮🇳' },
  { code: 'KRW', name: 'South Korean Won', nameIt: 'Won Sudcoreano', symbol: '₩', flag: '🇰🇷' },
  { code: 'SGD', name: 'Singapore Dollar', nameIt: 'Dollaro di Singapore', symbol: 'S$', flag: '🇸🇬' },
  { code: 'HKD', name: 'Hong Kong Dollar', nameIt: 'Dollaro di Hong Kong', symbol: 'HK$', flag: '🇭🇰' },
  { code: 'NZD', name: 'New Zealand Dollar', nameIt: 'Dollaro Neozelandese', symbol: 'NZ$', flag: '🇳🇿' },
  { code: 'ZAR', name: 'South African Rand', nameIt: 'Rand Sudafricano', symbol: 'R', flag: '🇿🇦' },
  { code: 'THB', name: 'Thai Baht', nameIt: 'Baht Tailandese', symbol: '฿', flag: '🇹🇭' },
  { code: 'IDR', name: 'Indonesian Rupiah', nameIt: 'Rupia Indonesiana', symbol: 'Rp', flag: '🇮🇩' },
  { code: 'MYR', name: 'Malaysian Ringgit', nameIt: 'Ringgit Malese', symbol: 'RM', flag: '🇲🇾' },
  { code: 'PHP', name: 'Philippine Peso', nameIt: 'Peso Filippino', symbol: '₱', flag: '🇵🇭' },
  { code: 'ILS', name: 'Israeli Shekel', nameIt: 'Shekel Israeliano', symbol: '₪', flag: '🇮🇱' },
  { code: 'ISK', name: 'Icelandic Krona', nameIt: 'Corona Islandese', symbol: 'kr', flag: '🇮🇸' },
];

/** Le otto in cima alla lista: sono quelle che l'ospite cerca nove volte su dieci. */
export const POPULAR_CURRENCIES: CurrencyInfo[] = CURRENCIES.slice(0, 8);

export function currencyInfo(code: string): CurrencyInfo {
  return CURRENCIES.filter((c) => c.code === code)[0] || CURRENCIES[0];
}

export function currencySymbol(code: string): string {
  return currencyInfo(code).symbol;
}

/** Tassi di riserva (base EUR) per quando la rete manca e la cache è vuota. */
export const FALLBACK_RATES: Record<string, number> = {
  EUR: 1, USD: 1.09, GBP: 0.86, CHF: 0.96, JPY: 163.5,
  CAD: 1.48, AUD: 1.66, CNY: 7.85, SEK: 11.2, NOK: 11.5,
  DKK: 7.46, PLN: 4.32, CZK: 25.2, HUF: 390, RON: 4.97,
  BGN: 1.96, HRK: 7.53, TRY: 35.2, BRL: 5.35, MXN: 18.7,
  INR: 90.8, KRW: 1420, SGD: 1.46, HKD: 8.52, NZD: 1.78,
  ZAR: 20.1, THB: 37.8, IDR: 16800, MYR: 5.05, PHP: 60.5,
  ILS: 3.95, ISK: 149,
};

export const STORAGE_KEY_RATES = 'travel-wallet-rates';
export const STORAGE_KEY_RATES_DATE = 'travel-wallet-rates-date';
/** L'ultima valuta scelta nel cambio: la riga "1 € = ..." di Esplora mostra quella. */
export const STORAGE_KEY_RATE_CODE = 'travel-wallet-rate-code';

export function readRateCode(): string {
  try {
    const code = localStorage.getItem(STORAGE_KEY_RATE_CODE);
    if (code && CURRENCIES.some((c) => c.code === code)) return code;
  } catch {
    /* storage bloccato: vale il dollaro */
  }
  return 'USD';
}

export function writeRateCode(code: string): void {
  try {
    localStorage.setItem(STORAGE_KEY_RATE_CODE, code);
  } catch {
    /* storage bloccato: la scelta vale per questa sessione */
  }
}

// --- Categorie ----------------------------------------------------------

/** Quattro, non sei: "alloggio" e "shopping" finivano sempre in "altro". */
export type ExpenseCategory = 'food' | 'transport' | 'activities' | 'other';

export interface ExpenseCategoryConfig {
  key: ExpenseCategory;
  icon: string;
  /** Colore della pastiglia quando è scelta e della barra nel riepilogo. */
  accent: string;
  chipOn: string;
  chipOff: string;
}

export const EXPENSE_CATEGORIES: ExpenseCategoryConfig[] = [
  {
    key: 'food',
    icon: '🍽️',
    accent: '#F97316',
    chipOn: 'border-orange-500 bg-orange-50 dark:bg-orange-900/25 text-orange-700 dark:text-orange-200',
    chipOff: 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300',
  },
  {
    key: 'transport',
    icon: '🚆',
    accent: '#3B82F6',
    chipOn: 'border-blue-500 bg-blue-50 dark:bg-blue-900/25 text-blue-700 dark:text-blue-200',
    chipOff: 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300',
  },
  {
    key: 'activities',
    icon: '🎟️',
    accent: '#10B981',
    chipOn: 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/25 text-emerald-700 dark:text-emerald-200',
    chipOff: 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300',
  },
  {
    key: 'other',
    icon: '🧾',
    accent: '#8B5CF6',
    chipOn: 'border-violet-500 bg-violet-50 dark:bg-violet-900/25 text-violet-700 dark:text-violet-200',
    chipOff: 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300',
  },
];

export function categoryConfig(key: string): ExpenseCategoryConfig {
  return EXPENSE_CATEGORIES.filter((c) => c.key === key)[0] || EXPENSE_CATEGORIES[3];
}

// --- Dati ---------------------------------------------------------------

export interface WalletBudget {
  /** Importo nella valuta scelta dall'ospite... */
  amount: number;
  currency: string;
  /** ...e lo stesso importo in euro, con cui si fanno tutti i conti. */
  amountEur: number;
  startDate: string;
  endDate: string;
}

export interface WalletExpense {
  id: string;
  amount: number;
  currency: string;
  amountEur: number;
  category: ExpenseCategory;
  name?: string;
  /** Giorno della spesa, "YYYY-MM-DD" (non un istante: le spese sono di un giorno). */
  date: string;
  createdAt: string;
  /** L'attività di "Il mio giorno" da cui è nata questa spesa. */
  activityId?: string;
}

export interface WalletData {
  v: 1;
  budget?: WalletBudget | null;
  expenses: WalletExpense[];
}

export const emptyWallet = (): WalletData => ({ v: 1, budget: null, expenses: [] });

export function newId(): string {
  return Math.random().toString(36).slice(2, 9) + Date.now().toString(36);
}

// --- Salvataggio e migrazione ------------------------------------------

const CATEGORY_FROM_LEGACY: Record<string, ExpenseCategory> = {
  food: 'food',
  transport: 'transport',
  activities: 'activities',
  accommodation: 'other',
  shopping: 'other',
  other: 'other',
};

/** Un istante ISO → il giorno "YYYY-MM-DD" a cui appartiene. */
function dayOf(value: string): string {
  const direct = /^(\d{4}-\d{2}-\d{2})/.exec(String(value || ''));
  if (direct) return direct[1];
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * I dati del vecchio Portafoglio (una chiave sola per tutti i soggiorni)
 * portati nella forma nuova: budget e spese sì, fotografie degli scontrini
 * no — erano megabyte di base64 nello storage per un'anteprima sfocata.
 * La chiave vecchia non si tocca: se qualcosa va storto è ancora lì.
 */
function migrateLegacyWallet(): WalletData | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(LEGACY_WALLET_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  try {
    const old = JSON.parse(raw) as {
      budget?: { totalBudget?: number; budgetCurrency?: string; totalBudgetEur?: number; startDate?: string; endDate?: string } | null;
      expenses?: Array<{
        id?: string; name?: string; amount?: number; originalAmount?: number;
        originalCurrency?: string; category?: string; date?: string;
      }>;
    };

    const today = dayOf(new Date().toISOString());
    const expenses: WalletExpense[] = (Array.isArray(old?.expenses) ? old.expenses : [])
      .map((e) => {
        const amountEur = Number(e?.amount) || 0;
        const currency = String(e?.originalCurrency || 'EUR');
        const amount = Number(e?.originalAmount) || amountEur;
        return {
          id: String(e?.id || newId()),
          amount,
          currency,
          amountEur,
          category: CATEGORY_FROM_LEGACY[String(e?.category || 'other')] || 'other',
          name: String(e?.name || '').trim() || undefined,
          date: dayOf(String(e?.date || '')) || today,
          createdAt: String(e?.date || new Date().toISOString()),
        };
      })
      .filter((e) => e.amountEur > 0);

    const oldBudget = old?.budget;
    const amountEur = Number(oldBudget?.totalBudgetEur) || 0;
    const budget: WalletBudget | null = oldBudget && amountEur > 0
      ? {
        amount: Number(oldBudget.totalBudget) || amountEur,
        currency: String(oldBudget.budgetCurrency || 'EUR'),
        amountEur,
        startDate: dayOf(String(oldBudget.startDate || '')) || today,
        endDate: dayOf(String(oldBudget.endDate || '')) || '',
      }
      : null;

    if (!budget && expenses.length === 0) return null;
    return { v: 1, budget, expenses };
  } catch {
    return null;
  }
}

function sanitize(parsed: unknown): WalletData {
  const data = parsed as Partial<WalletData> | null;
  const expenses = Array.isArray(data?.expenses) ? (data as WalletData).expenses : [];
  return {
    v: 1,
    budget: data?.budget && Number(data.budget.amountEur) > 0 ? data.budget : null,
    expenses: expenses.filter((e) => e && typeof e.id === 'string'),
  };
}

/** Legge i dati del soggiorno; la prima volta porta dentro quelli vecchi. */
export function loadWallet(ref: string): WalletData {
  try {
    const raw = localStorage.getItem(walletStorageKey(ref));
    if (raw) return sanitize(JSON.parse(raw));
  } catch {
    return emptyWallet();
  }

  // Nessun dato nuovo: è il primo avvio dopo la riscrittura. I dati vecchi
  // si copiano una volta sola, nella prima chiave che li chiede.
  if (legacyMigrationDone('wallet')) return emptyWallet();
  const migrated = migrateLegacyWallet();
  markLegacyMigrationDone('wallet');
  if (migrated) {
    saveWallet(ref, migrated);
    return migrated;
  }
  return emptyWallet();
}

export function saveWallet(ref: string, data: WalletData): void {
  try {
    localStorage.setItem(walletStorageKey(ref), JSON.stringify(data));
  } catch {
    /* storage pieno o bloccato: i dati restano solo in pagina */
  }
}
